import { useState } from "react";
import Reveal from "@/components/home/Reveal";
import { useRegisterModal } from "@/components/home/RegisterModalProvider";
import { useExchangeRate, toMxn } from "@/components/home/ExchangeRateProvider";
import { mxn } from "@/lib/whatsapp";
import pricing from "@/content/pricing.json";
import { TrendingDown, ArrowRight } from "lucide-react";

const PRO = pricing.plans.find((p) => p.id === "pro");

const Range = ({ label, value, min, max, step, format, onChange, testId }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <label className="block">
      <div className="flex justify-between items-baseline gap-4 mb-3">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <span className="font-heading font-bold text-xl text-brand-carbon" data-testid={`${testId}-value`}>{format(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-range" style={{ "--pct": `${pct}%` }} data-testid={testId} aria-label={label} />
      <div className="flex justify-between text-xs text-gray-400 mt-1.5"><span>{format(min)}</span><span>{format(max)}</span></div>
    </label>
  );
};

export const CommissionCalculator = () => {
  const { openModal } = useRegisterModal();
  const { rate } = useExchangeRate();
  const [sales, setSales] = useState(80000);
  const [rateFee, setRateFee] = useState(25);
  const monthly = sales * (rateFee / 100);
  const yearly = monthly * 12;
  const proMxn = toMxn(PRO.usd_monthly, rate);
  const savings = proMxn != null ? yearly - proMxn * 12 : yearly;

  return (
    <section id="calculadora" className="mt-section bg-white" data-testid="calculator-section">
      <div className="mt-container">
        <Reveal className="max-w-2xl">
          <p className="text-brand-orange font-semibold text-sm uppercase tracking-wider">Calculadora</p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl leading-tight">¿Cuánto pierdes cada mes pagando comisión?</h2>
          <p className="mt-4 text-base md:text-lg text-gray-600">Mueve los controles con tus números reales. El resultado se actualiza al instante.</p>
        </Reveal>
        <Reveal delay={120} className="mt-10 grid lg:grid-cols-[1fr_0.9fr] gap-6 lg:gap-8">
          <div className="rounded-3xl border border-orange-100 bg-brand-cream/60 p-7 lg:p-9 space-y-9">
            <Range label="Ventas mensuales por delivery (MXN)" value={sales} min={10000} max={500000} step={5000} format={mxn} onChange={setSales} testId="calc-sales-slider" />
            <Range label="Comisión que te cobran hoy (%)" value={rateFee} min={15} max={30} step={1} format={(v) => `${v}%`} onChange={setRateFee} testId="calc-rate-slider" />
          </div>
          <div className="rounded-3xl bg-brand-carbon text-white p-7 lg:p-9 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-orange/25 blur-3xl" />
            <div className="relative">
              <p className="text-sm text-gray-300 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-400" /> Pierdes hoy</p>
              <p className="mt-1 font-heading font-extrabold text-4xl sm:text-5xl text-red-400 leading-none" data-testid="calc-monthly-loss">{mxn(monthly)} <span className="text-lg text-gray-400 font-semibold">/ mes</span></p>
              <p className="mt-6 text-sm text-gray-300">Pierdes al año</p>
              <p className="mt-1 font-heading font-bold text-3xl leading-none" data-testid="calc-yearly-loss">{mxn(yearly)}</p>
              <div className="mt-7 rounded-2xl bg-brand-green/15 border border-brand-green/30 p-4">
                <p className="text-sm text-green-200">Con MenuThere Pro ahorrarías</p>
                <p className="mt-1 font-heading font-extrabold text-3xl sm:text-4xl text-brand-green leading-none" data-testid="calc-savings">{mxn(Math.max(savings, 0))} <span className="text-base text-green-200 font-semibold">/ año</span></p>
                <p className="mt-2 text-xs text-green-100/80" data-testid="calc-savings-note">
                  {proMxn != null ? `Ya descontado el plan Pro: $${PRO.usd_monthly} USD ≈ ${mxn(proMxn)} MXN al mes (FIX Banxico de hoy). Con el plan Free el ahorro es total.` : `Ya descontado el plan Pro ($${PRO.usd_monthly} USD/mes). Con el plan Free el ahorro es total.`}
                </p>
              </div>
            </div>
            <button onClick={() => openModal({ source: "plan" })} className="btn-mt btn-mt-primary mt-8 relative" data-track="calculadora-empezar" data-testid="calc-cta-button">
              Deja de regalar ese dinero → Empezar gratis <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CommissionCalculator;
